import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    EffectRef,
    inject,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CeTemplateViewHeaderComponent, ContentSplitViewLayoutService } from '@kempower-nx-workspace/layout-templates';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import {
    EnrichedFrontendNotification,
    fetchedNotifications,
    GetNotificationEmailSentTimestampResponse,
    NotificationDataService,
} from '../../data-access/notification-data/notification-data.service';
import {
    CeBreadcrumbComponent,
    CeButtonComponent,
    CeContextMenuImports,
    CeEmptyStateImports,
    CeIconComponent,
    CeListImports,
    CeSkeletonLoaderImports,
    CeTooltipDirective,
    RouterLinkInput,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { asapScheduler, filter, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@kempower-nx-workspace/ce-environment';
import { formatTimeAndDate } from '@kempower-nx-workspace/global-toolkit';
import { last as _last } from 'lodash-es';
import {
    ArchiveNotificationsResponse,
    ArchiveStatus,
    ExternalNotificationsApiServiceObservable,
    FrontendEvent,
    MarkReadNotificationsResponse,
    ReadStatus,
} from '@kempoweroyj/ngx-external-notifications-api-client';
import { LocationsApiService, LocationsApiServiceObservable } from '@kempoweroyj/ngx-locations-api-client';
import {
    NotificationSubscriptionAboutToExpireContentComponent,
} from '../../ui/notification-subscription-about-to-expire-content/notification-subscription-about-to-expire-content.component';
import {
    NotificationVehicleTelematicsContentComponent,
} from '../../ui/notification-vehicle-telematics-content/notification-vehicle-telematics-content.component';
import {
    NotificationDepotOperationContentComponent,
} from '../../ui/notification-depot-operation-content/notification-depot-operation-content.component';
import { NotificationPmcFaultContentComponent } from '../../ui/notification-pmc-fault-content/notification-pmc-fault-content.component';

@Component({
    selector: 'ce-app-notifications-notification',
    imports: [
        CommonModule,
        CeButtonComponent,
        CeContextMenuImports,
        CeIconComponent,
        CeListImports,
        CeSkeletonLoaderImports,
        CeTemplateViewHeaderComponent,
        RouterLink,
        CeEmptyStateImports,
        CeBreadcrumbComponent,
        CeTooltipDirective,
        NotificationSubscriptionAboutToExpireContentComponent,
        NotificationVehicleTelematicsContentComponent,
        NotificationDepotOperationContentComponent,
        NotificationPmcFaultContentComponent,
    ],
    providers: [
        NotificationDataService,
        ExternalNotificationsApiServiceObservable,
        LocationsApiService,
        LocationsApiServiceObservable,
    ],
    templateUrl: './notification.component.html',
    styleUrl: './notification.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationComponent implements OnInit {
    readonly #router = inject(Router);
    readonly #activatedRoute = inject(ActivatedRoute);
    readonly #splitViewLayoutService = inject(ContentSplitViewLayoutService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #notificationDataService = inject(NotificationDataService);

    notificationFeed: WritableSignal<EnrichedFrontendNotification> = signal({} as EnrichedFrontendNotification);
    notificationIsFound: WritableSignal<boolean | undefined> = signal(undefined);

    notificationId = this.#activatedRoute.snapshot.paramMap.get('notificationId') as string;
    notificationStatus = this.#activatedRoute.snapshot.parent?.paramMap.get('notificationStatus') as ArchiveStatus;
    detailsInClassicUrl = '';
    detailsRouterLink: RouterLinkInput = undefined;
    detailsQueryParams: Record<string, string> | undefined = undefined;
    detailsUrl = '';
    linkToClassicLabel = '';
    linkLabel = '';
    notificationListUrl: `/${string}` = `/notifications/${this.notificationStatus}`;

    readonly #getEmailTimestampAgainTrigger = new Subject<void>();
    emailTimestampFeed = this.#getEmailTimestampAgainTrigger.pipe(
        switchMap(() => this.#notificationDataService.getNotificationEmailSentTimestamp$(this.notificationId)),
    );
    emailTimestamp: Signal<GetNotificationEmailSentTimestampResponse | undefined> = toSignal(this.emailTimestampFeed);
    readStatusSignal: Signal<MarkReadNotificationsResponse | undefined> = toSignal(
        this.#notificationDataService.readStatusSubject$(),
    );
    archivedStatusSignal: Signal<ArchiveNotificationsResponse | undefined> = toSignal(
        this.#notificationDataService.archiveStatusSubject$(),
    );

    hasLoaded: Signal<boolean> = computed(() => {
        return this.notificationIsFound() !== undefined;
    });

    hasMultipleEvents: Signal<boolean> = computed(() => {
        return this.notification().events.length > 1;
    });

    hasSingleEvent: Signal<boolean> = computed(() => {
        return this.notification().events.length === 1;
    });

    showResolutionReason: Signal<boolean> = computed(() => {
        return (
            this.notification().events.length === 1 &&
            !!this.notification().events[0]?.resolvedTimestamp &&
            !!this.notification().events[0]?.resolutionReason
        );
    });

    isScheduledSessionEndedUnexpectedly: Signal<boolean> = computed(() => {
        return this.notification().eventType === 'scheduled-session-ended-unexpectedly';
    });

    isSystemEvent: Signal<boolean> = computed(() => {
        return this.notification().eventType === 'subscription-is-about-to-expire';
    });

    isPmcFaultEvent: Signal<boolean> = computed(() => {
        return this.notification().eventType === 'pmc-fault';
    });

    isDepotOperationEvent: Signal<boolean> = computed(() => {
        return !this.isSystemEvent() && !this.isPmcFaultEvent();
    });

    isVehicleTelematicsNotification: Signal<boolean> = computed(() => {
        return this.notification().eventType === 'vehicle-soc-low';
    });

    tooltipText: Signal<string> = computed(() => {
        const eventType = this.notificationFeed().eventType;
        if (eventType === 'subscription-is-about-to-expire') {
            return 'Opens in MyKempower';
        } else if (eventType === 'vehicle-soc-low') {
            return 'Opens in ChargEye beta';
        } else if (eventType === 'pmc-fault') {
            return 'Opens in ChargEye classic';
        } else {
            return 'Opens in ChargEye classic';
        }
    });

    isSubscriptionExpired: Signal<boolean> = computed(() => {
        const daysUntilExpiration = this.notification().events[0]?.metadata?.['daysUntilExpiration'];
        return daysUntilExpiration !== undefined && daysUntilExpiration < 0;
    });

    showClassicLink: Signal<boolean> = computed(() => {
        return !this.detailsRouterLink && (
            (!this.isDepotOperationEvent() && !this.isVehicleTelematicsNotification()) ||
            (this.isSystemEvent() && this.isSubscriptionExpired())
        );
    });

    notification: Signal<EnrichedFrontendNotification> = computed((): EnrichedFrontendNotification => {
        const vehicleNames: Array<string> = [];
        const events: Array<FrontendEvent> = [];
        const unknownEvLabel = 'unknown EV';

        if (this.notificationFeed().events) {
            // Check if this is a system event - skip depot operation processing
            const isSystemEvent = this.notificationFeed().eventType === 'subscription-is-about-to-expire';
            const isPmcFault = this.notificationFeed().eventType === 'pmc-fault';

            if (isSystemEvent) {
                // System events link to MyKempower
                const envPrefix = environment.appConfig.envType === 'prod' ? '' : `${environment.appConfig.envType}.`;
                this.detailsInClassicUrl = `https://${envPrefix}mykempower.com`;
                this.linkToClassicLabel = 'Go to MyKempower';
                // Pass through events as-is without vehicle processing
                events.push(...this.notificationFeed().events);
            } else if (isPmcFault) {
                // PMC fault events link to the alert on the station page (or just the station if no alertId)
                const stationId = this.notificationFeed().events[0]?.scope?.['stationId']
                    ?? this.notificationFeed().events[0]?.metadata?.['stationId'];
                const alertId = this.notificationFeed().events[0]?.scope?.['alertId'];
                const stationUrl = `https://${environment.appConfig.apiBaseUrl}/locations/l/station/${stationId}`;
                this.detailsInClassicUrl = alertId ? `${stationUrl}/status(d:alert/${alertId})` : stationUrl;
                this.linkToClassicLabel = 'See charging station';
                // Pass through events as-is without vehicle processing
                events.push(...this.notificationFeed().events);
            } else if (this.notificationFeed().eventType === 'vehicle-soc-low') {
                // Vehicle fleet operations events link to EV telematics
                // Falls through to vehicle name processing below since vehicle names are relevant for fleet operations
                this.detailsUrl = '/ev-telematics';
                this.linkToClassicLabel = 'View EV telematics';
            } else {
                // Depot operations events
                // Redirect user either to 'Ongoing and past' or 'Scheduled' sessions list.
                //
                // If there are multiple events:
                //    - If all events have 'parentId', redirect to 'Ongoing and past' listing.
                //    - If some event is missing 'parentId', redirect to 'Scheduled' listing.
                //
                // If there is only one event:
                //    - If it has 'parentId', redirect to 'Ongoing and past' listing and open 'charging-session' dialog.
                //    - If it doesn't have 'parentId' but has 'scheduleId', redirect to 'Scheduled' listing and open 'charging-scheduled-session' dialog.

                const parentIds: string[] = [];
                const scheduleIds: string[] = [];
                const events = this.notificationFeed().events;
                const isSingleEvent = events.length === 1;

                for (const event of events) {
                    const parentId = event?.metadata?.['parentId'];
                    if (parentId && parentId !== 'undefined') {
                        parentIds.push(parentId);
                    }
                    const scheduleId = event?.scope?.['scheduleId'];
                    if (scheduleId) {
                        scheduleIds.push(scheduleId);
                    }
                }
                if (isSingleEvent) {
                    if (parentIds.length === 1) {
                        this.detailsRouterLink = ['/', {
                            outlets: {
                                primary: ['charging-network', 'sessions'],
                                dialog: ['charging-session', parentIds[0]],
                            },
                        }];
                        this.detailsQueryParams = { sessionIds: parentIds[0] };
                    } else if (scheduleIds.length === 1) {
                        this.detailsRouterLink = ['/', {
                            outlets: {
                                primary: ['charging-network', 'sessions', 'scheduled'],
                                dialog: ['charging-scheduled-session', scheduleIds[0]],
                            },
                        }];
                        this.detailsQueryParams = { scheduleIds: scheduleIds[0] };
                    } else {
                        this.detailsRouterLink = '/charging-network/sessions/scheduled';
                    }
                    this.linkLabel = 'See charging session';
                } else {
                    if (parentIds.length > 0 && parentIds.length === events.length) {
                        this.detailsRouterLink = '/charging-network/sessions';
                        this.detailsQueryParams = { sessionIds: parentIds.join(',') };
                    } else {
                        this.detailsRouterLink = '/charging-network/sessions/scheduled';
                        this.detailsQueryParams = { scheduleIds: scheduleIds.join(',') };
                    }
                    this.linkLabel = 'See charging sessions';
                }
            }

            // Process vehicle names for depot operations and vehicle fleet operations
            // System events and PMC fault events skip this as they don't have associated vehicles
            let unknownEvCount = 0;
            if (!isSystemEvent && !isPmcFault) {
                this.notificationFeed().events.forEach((event) => {
                    if (event?.metadata?.['vehicleName']) {
                        vehicleNames.push(event?.metadata?.['vehicleName']);
                        events.push(event);
                    } else {
                        unknownEvCount++;
                        events.push({ ...event, metadata: { ...event.metadata, vehicleName: unknownEvLabel } });
                    }
                });

                // Alphabetize "vehicleNames" or "events based on vehicleNames", so that unknown vehicles are last
                vehicleNames.sort((a: string, b: string) => this.compareIgnoreCase(a, b));
                events.sort((a: FrontendEvent, b: FrontendEvent) => {
                    // Sort item(s) with name 'unknownEvLabel' always as last
                    if (a.metadata?.['vehicleName'] === unknownEvLabel) {
                        return 1;
                    }
                    if (b.metadata?.['vehicleName'] === unknownEvLabel) {
                        return -1;
                    }
                    return this.compareIgnoreCase(a.metadata?.['vehicleName'], b.metadata?.['vehicleName']);
                });

                if (unknownEvCount === 1) {
                    vehicleNames.push(`1 ${unknownEvLabel}`);
                }
                if (unknownEvCount > 1) {
                    vehicleNames.push(`${unknownEvCount} unknown EVs`);
                }
            } // End of if (!isSystemEvent) block
        } // End of if (this.notificationFeed().events)

        const enrichedNotification = {
            archived: this.notificationFeed().archived,
            body: this.notificationFeed().body,
            eventResolvedTimestamp:
                this.notificationFeed().eventResolvedTimestamp !== undefined
                    ? formatTimeAndDate(this.notificationFeed().eventResolvedTimestamp)
                    : '',
            eventState: this.notificationFeed().eventState,
            eventTimestamp: formatTimeAndDate(this.notificationFeed().eventTimestamp),
            eventType: this.notificationFeed().eventType,
            events: events,
            id: this.notificationFeed().id,
            notificationTimestamp: formatTimeAndDate(this.notificationFeed().notificationTimestamp),
            notificationType: this.notificationFeed().notificationType,
            siteName: this.notificationFeed().siteName,
            status: this.notificationFeed().status,
            title: this.notificationFeed().title,
            vehicleNames: vehicleNames,
        };

        return enrichedNotification;
    });

    ngOnInit(): void {
        this.#splitViewLayoutService.setSecondViewVisibility(true);
    }

    compareIgnoreCase(strA: string, strB: string) {
        return strA.toLowerCase().localeCompare(strB.toLowerCase());
    }

    updateNotificationData(idToCompare: string): void {
        // Check if notification exists and update 'notificationIsFound' and 'notificationFeed' based on it
        const resultArray: Array<boolean> = fetchedNotifications().map((notification: EnrichedFrontendNotification) => {
            if (notification.id === idToCompare) {
                asapScheduler.schedule(() => {
                    this.notificationFeed.set(notification);
                });
            }
            return notification.id === idToCompare;
        });
        if (resultArray.length > 0) {
            const isFound = resultArray.includes(true);
            asapScheduler.schedule(() => {
                this.notificationIsFound.set(isFound);
            });
            if (isFound) {
                this.#getEmailTimestampAgainTrigger.next();
            }
        }
    }

    updateReadStatus(markAsRead: boolean): void {
        this.#notificationDataService.updateNotificationReadStatus({
            markAll: false,
            notificationIds: [this.notificationId],
            read: markAsRead,
            archiveStatusFilter: this.notificationStatus,
        });
    }

    updateArchivedStatus(markAsArchived: boolean): void {
        this.#notificationDataService.updateNotificationArchivedStatus({
            markAll: false,
            notificationIds: [this.notificationId],
            archiveStatus: markAsArchived,
        });
    }

    updateNotificationDataOnReadStatusChange: EffectRef = effect(() => {
        if (this.readStatusSignal() !== undefined) {
            asapScheduler.schedule(() => {
                this.notificationFeed.set({
                    ...this.notificationFeed(),
                    status: this.readStatusSignal()?.readStatus as ReadStatus,
                });
            });
        }
    });

    updateNotificationDataOnArchiveStatusChange: EffectRef = effect(() => {
        if (this.archivedStatusSignal() !== undefined) {
            asapScheduler.schedule(() => {
                this.notificationFeed.set({
                    ...this.notificationFeed(),
                    archived: this.archivedStatusSignal()?.archiveStatus as boolean,
                });
            });
        }
    });

    updateNotificationShown: EffectRef = effect(() => {
        if (fetchedNotifications()) {
            this.updateNotificationData(this.notificationId);
        }
    });

    updateNotificationDataOnRouteChange: EffectRef = effect(() => {
        // Update notification data when another notification is selected
        this.#router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((event) => {
                const currentNotificationId = _last(event.url.split('/')) as string;

                window.scrollTo(0, 0);
                this.notificationId = currentNotificationId;
                this.updateNotificationData(currentNotificationId);
            });
    });
}
